import { STAGES, STATUS } from './constants';

/** 与后端一致按 Unicode 码点计数（emoji 算 1 个字） */
export const countChars = (text) => (text ? Array.from(text).length : 0);

const randomHex = () =>
    Math.floor(Math.random() * 0x100000000)
        .toString(16)
        .padStart(8, '0');

/** 一次“确认生成”的请求编号；内容不变时复用，重复点击或超时后重发不会重复创建任务 */
export const newRequestId = () =>
    window.crypto?.randomUUID
        ? window.crypto.randomUUID().replace(/-/g, '')
        : `${Date.now().toString(16)}${randomHex()}${randomHex()}`;

export const isMaterialStatus = (status) =>
    status === STATUS.MATERIAL_PROCESSING ||
    status === STATUS.MATERIAL_READY ||
    status === STATUS.MATERIAL_FAILED;

/** 资料整理中已确认生成，整理完成（且已支付）后后台自动提交 */
export const isQueued = (view) =>
    Boolean(view?.autoSubmit) &&
    (view.status === STATUS.MATERIAL_PROCESSING || view.status === STATUS.MATERIAL_READY);

/** 已确认生成、还没付款（含资料整理中先确认的文档任务） */
export const needsPayment = (view) =>
    Boolean(view?.payment?.orderNo) &&
    !view.payment.paid &&
    (view.status === STATUS.AWAITING_PAYMENT || isQueued(view));

/** 文档任务在提交前停留在第一步；已提交、待支付或排队自动生成的进入结果页 */
export const belongsToResult = (view) =>
    Boolean(view) &&
    (isQueued(view) ||
        (!isMaterialStatus(view.status) &&
            !(view.inputType === 'file' && view.status === STATUS.SUBMIT_FAILED)));

/** 资料已整理好，可以（重新）确认生成；待支付时回来改选项也算 */
export const materialReady = (view) =>
    Boolean(view?.material) &&
    (view.status === STATUS.MATERIAL_READY ||
        view.status === STATUS.SUBMIT_FAILED ||
        view.status === STATUS.AWAITING_PAYMENT);

/** 当前处于第几个生成阶段（0 规划、1 撰写、2 渲染、3 完成） */
export function stageIndex(view) {
    if (!view) return 0;
    if (view.status === STATUS.COMPLETED) return STAGES.length - 1;
    const index = STAGES.findIndex((stage) => stage.key === view.progress?.stage);
    return index < 0 ? 0 : index;
}

const ratio = (value, total) => (total > 0 ? Math.min(1, Math.max(0, (value || 0) / total)) : 0);

/** 估算整体进度百分比：规划 0-15%、撰写 15-75%、渲染 75-100% */
export function progressPercent(view) {
    if (!view) return 0;
    if (view.status === STATUS.COMPLETED) return 100;
    const progress = view.progress || {};
    const total = progress.plannedPages || progress.requestedPages || view.options?.pageCount || 0;
    switch (progress.stage) {
        case 'content':
            return Math.round(15 + 60 * ratio(progress.writtenPages, total));
        case 'rendering':
            return Math.round(75 + 24 * ratio(progress.renderedPages, total));
        case 'done':
            return 99;
        default:
            return total > 0 ? 12 : 5;
    }
}

/** 页面缩略图槽位：已创作的页面在前，计划中尚未创作的页面以占位补齐 */
export function pageSlots(view) {
    const pages = [...(view?.pages || [])].sort((a, b) => a.index - b.index);
    const progress = view?.progress || {};
    const planned = progress.plannedPages || progress.requestedPages || pages.length || 0;
    const slots = pages.map((page) => ({ key: page.pageId, page }));
    for (let index = pages.length; index < planned; index += 1) {
        slots.push({ key: `pending-${index}`, page: null });
    }
    return slots;
}

/** 删除一个章节，并去掉只被这一节引用的证据；从未被引用的证据保留 */
export function removeSection(material, sectionId) {
    const sections = (material?.sections || []).filter((section) => section.id !== sectionId);
    const removed = (material?.sections || []).find((section) => section.id === sectionId);
    const stillUsed = new Set(sections.flatMap((section) => section.evidence_ids || []));
    const dropped = new Set((removed?.evidence_ids || []).filter((id) => !stillUsed.has(id)));
    const next = { ...material, sections };
    const evidence = (material?.evidence || []).filter((item) => !dropped.has(item.id));
    if (evidence.length) next.evidence = evidence;
    else delete next.evidence;
    return next;
}

/** 提交给后端的生成选项（空值省略，由后端决定默认） */
export function buildSubmitPayload({ settings, templateId, prompt }) {
    const payload = {
        templateId,
        scenario: settings.scenario,
        generateNotes: Boolean(settings.generateNotes),
    };
    const audience = (settings.audience || '').trim();
    if (audience) payload.audience = audience;
    if (Number.isInteger(settings.durationMinutes))
        payload.durationMinutes = settings.durationMinutes;
    if (Number.isInteger(settings.pageCount)) payload.pageCount = settings.pageCount;
    const text = (prompt || '').trim();
    if (text) payload.prompt = text;
    return payload;
}

/** 后端错误信封里的 errorCode（HTTP 非 2xx 时位于 response.data.data） */
export const errorCodeOf = (error) => error?.response?.data?.data?.errorCode || null;

export const errorMessageOf = (error, fallback) =>
    error?.response?.data?.msg || error?.message || fallback;

export const sanitizeFileName = (name, fallback = '答辩PPT') => {
    const cleaned = String(name || '')
        // eslint-disable-next-line no-control-regex
        .replace(/[\\/:*?"<>|\u0000-\u001f]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 60)
        .trim();
    return cleaned || fallback;
};

export const fileExtension = (name) => {
    const index = String(name || '').lastIndexOf('.');
    return index < 0 ? '' : name.slice(index + 1).toLowerCase();
};

export const formatElapsed = (ms) => {
    const seconds = Math.max(0, Math.floor(ms / 1000));
    if (seconds < 60) return `${seconds} 秒`;
    return `${Math.floor(seconds / 60)} 分 ${seconds % 60} 秒`;
};

export const formatTime = (value) => {
    if (!value) return '';
    // 后端返回 "2026-10-21 10:00:00"，Safari 不认空格分隔，统一换成 T
    const date = new Date(typeof value === 'string' ? value.replace(' ', 'T') : value);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getMonth() + 1}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** 金额（元）显示为 ¥39.9 / ¥40；无效值返回空串 */
export const formatPrice = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const number = Number(value);
    if (!Number.isFinite(number)) return '';
    return `¥${number.toFixed(2).replace(/\.?0+$/, '')}`;
};

// 写作订单带来的答辩PPT兑换码（/ppt?code=MLP...）：地址栏会被 ?task= 替换，所以记在本标签页
const CODE_KEY = 'lgfb.ppt-redeem-code';
const CODE_PATTERN = /^[A-Za-z0-9_-]{4,64}$/;

export function rememberRedeemCode(fromUrl) {
    const code = String(fromUrl || '').trim();
    try {
        if (CODE_PATTERN.test(code)) {
            window.sessionStorage.setItem(CODE_KEY, code);
            return code;
        }
        const saved = window.sessionStorage.getItem(CODE_KEY) || '';
        return CODE_PATTERN.test(saved) ? saved : '';
    } catch {
        return CODE_PATTERN.test(code) ? code : '';
    }
}

export function forgetRedeemCode() {
    try {
        window.sessionStorage.removeItem(CODE_KEY);
    } catch {
        // 存储不可用时忽略
    }
}

/**
 * 模板示例页：优先用 previewPages（墨得字段），其次 previewUrls（字符串数组），都没有就只有封面。
 */
export function templatePreviewPages(template) {
    if (Array.isArray(template?.previewPages) && template.previewPages.length) {
        return template.previewPages.map((page, index) => ({
            pageNumber: page.pageNumber || index + 1,
            title: page.title || `示例第 ${index + 1} 页`,
            imageUrl: page.imageUrl,
        }));
    }
    if (Array.isArray(template?.previewUrls) && template.previewUrls.length) {
        return template.previewUrls.map((url, index) => ({
            pageNumber: index + 1,
            title: `示例第 ${index + 1} 页`,
            imageUrl: url,
        }));
    }
    return [{ pageNumber: 1, title: template?.name || '封面', imageUrl: template?.coverUrl }];
}
